
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarFooter,
  useSidebar as useShadcnSidebar,
} from "@/components/ui/sidebar";
import { SidebarNavGroup } from './sidebar/SidebarNavGroup';
import { groupedItems, categoryLabels } from './sidebar/SidebarData';
import { SubscriptionStatusIndicator } from './sidebar/SubscriptionAwareSidebar';
import { useSidebar } from '@/contexts/SidebarContext';
import { Button } from '@/components/ui/button';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { MobileSidebarContent } from './sidebar/MobileSidebarContent';
import { useIsMobile } from '@/hooks/use-mobile';

export function AppSidebar() {
  const location = useLocation();
  const { setOpen, open, toggleSidebar } = useSidebar();
  const { isMobile } = useShadcnSidebar();

  // Auto-collapse sidebar on navigation for mobile/tablet views
  useEffect(() => {
    const handleRouteChange = () => {
      const isMobileScreen = window.innerWidth < 1024;
      
      if (isMobileScreen) {
        requestAnimationFrame(() => {
          setOpen(false);
        });
      }
    };

    handleRouteChange();
  }, [location.pathname, setOpen]);

  // Add keyboard shortcut for toggling sidebar
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'b') {
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleSidebar]);

  return (
    <Sidebar 
      variant="sidebar" 
      collapsible="offcanvas"
      className="border-r"
      role="navigation"
      aria-label="Main navigation"
    >
      <SidebarHeader className="border-b p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">EquiForge</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleSidebar}
            className="h-8 w-8 p-0"
            title={`${open ? 'Collapse' : 'Expand'} sidebar (Ctrl+B)`}
          >
            {open ? (
              <PanelLeftClose className="h-4 w-4" />
            ) : (
              <PanelLeftOpen className="h-4 w-4" />
            )}
            <span className="sr-only">
              {open ? 'Collapse sidebar' : 'Expand sidebar'}
            </span>
          </Button>
        </div>
        
        {/* Add subscription status indicator */}
        <SubscriptionStatusIndicator />
      </SidebarHeader>

      <SidebarContent className={isMobile ? "p-0" : "px-2 py-2"}>
        {isMobile ? (
          <MobileSidebarContent />
        ) : (
          Object.entries(groupedItems).map(([category, items]) => {
            // Only render categories that have items
            if (!items || items.length === 0) return null;
            
            return (
              <SidebarNavGroup
                key={category}
                category={category}
                label={categoryLabels[category as keyof typeof categoryLabels]}
                items={items}
              />
            );
          })
        )}
      </SidebarContent>

      <SidebarFooter className="border-t p-4">
        <div className="text-xs text-muted-foreground text-center">
          <p>EquiForge™ v2.0</p>
          <p className="mt-1">Enterprise Analytics</p>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
